import Vue from "vue";
import { API } from "@memberpoint/mp-frontend-core";
import isEmpty from "lodash/isEmpty";

const SimpleAPIResponse = API.SimpleAPIResponse;
const SimpleAPIValidationError = API.SimpleAPIErrors.SimpleAPIValidationError;
const BaseError = API.SimpleAPIErrors.BaseError;

/**
 * A VueJS mixin with helper functions that assist in form validation.
 */
export default {
  methods: {
    /**
     * Clear the reference errors if it contains the `clearErrors` function.
     *
     * @param refName
     */
    clearRefErrors(refName) {
      if (this.refExists(refName, "clearErrors")) {
        const ref = this.getRef(refName);
        ref.clearErrors();
      }
    },

    /**
     * Returns the reference component from the given name or NULL
     * if it does not exist.
     *
     * @param {string} refName
     * @return {null|Vue}
     */
    getRef(refName) {
      if (this.$refs.hasOwnProperty(refName)) {
        let ref = this.$refs[refName];

        if (ref instanceof Vue) {
          return ref;
        } else if (Array.isArray(ref) && ref.length > 0) {
          /*
           * When a reference is set in a v-for then the reference is set as an array.
           */
          return ref[0];
        }
      }

      return null;
    },

    /**
     * Check if a reference field ($refs) is vue component and is valid, and the field itself reports isValid()
     * @param refName {string} The name of the $refs field
     * @returns {boolean} True if the field is valid
     */
    refIsValid(refName) {
      /*
        - Check to see if the $refs array has a component with that name
        - Make sure the ref is a vue component
        - Make sure the component has a method called isValid
        - Make sure isValid() returns true.
       */

      if (!this.refExists(refName)) {
        return false;
      }

      if (typeof this.$refs[refName].isValid === "function") {
        return this.$refs[refName].isValid();
      } else if (typeof this.$refs[refName].isValidValue === "function") {
        return this.$refs[refName].isValidValue();
      } else if (typeof this.$refs[refName].isValid === "boolean") {
        return this.$refs[refName].isValid;
      }

      return true;
    },

    /**
     * Checks to see if a ref exists, that's a vue component, and optionally if it has a specific method.
     * @param refName
     * @param method {String|null}
     * @returns {boolean}
     */
    refExists(refName, method = null) {
      const ref = this.getRef(refName);

      if (ref === null) {
        return false;
      }

      if (method !== null) {
        return method in ref;
      }

      return true;
    },

    /**
     * Check if a reference field ($refs) is vue component and is valid, and the field itself reports isValid()
     * @see {refIsValid}
     * @param fields {string}
     * @returns {boolean} True if all of the fields are valid.
     */
    refsAreValid(...fields) {
      /* Loop through all the fields to check if they are valid.
         This allows us to validate all fields, instead of returning on the first fail
       */
      for (const i in fields) {
        if (fields.hasOwnProperty(i)) {
          if (!this.refIsValid(fields[i])) {
            return false;
          }
        }
      }

      return true;
    },

    /**
     * Process form validation errors for mapped reference fields.
     *
     * @param {SimpleAPIResponse} simpleAPIResponse The APIResponse to parse
     * @param  {Object.<string, string>} fieldMap A {Ref: FieldName} map of fields to process.
     * @param {String|null} genericErrorDataField The Generic Error data prop to set all other errors on.
     */
    processFormValidationErrors(
      simpleAPIResponse,
      fieldMap,
      genericErrorDataField = null
    ) {
      /*
       * If the object provided is not a SimpleAPIResponse then exit.
       */
      if (!(simpleAPIResponse instanceof SimpleAPIResponse)) {
        if (genericErrorDataField !== null) {
          this.$set(
            this,
            genericErrorDataField,
            API.SimpleAPIErrorParser.parse(simpleAPIResponse)
          );
        }
        return;
      }

      /*
       * Loop through all errors and build up error arrays.
       */
      const apiErrors = simpleAPIResponse.getErrors();

      /**
       *
       * @type {SimpleAPIValidationError[]}
       */
      const validationErrors = [];

      /**
       *
       * @type {BaseError[]|SimpleAPIError[]}
       */
      const otherErrors = [];

      for (let i in apiErrors) {
        if (apiErrors.hasOwnProperty(i)) {
          if (apiErrors[i] instanceof SimpleAPIValidationError) {
            validationErrors.push(apiErrors[i]);
          } else {
            otherErrors.push(apiErrors[i]);
          }
        }
      }

      /*
       * Parse each validation field
       */
      for (let i in validationErrors) {
        if (validationErrors.hasOwnProperty(i)) {
          let fieldName = validationErrors[i].getField();
          let errorMsg = validationErrors[i].getMessage();

          // if no fieldMap provided then lets just assume that
          // the fieldName is also the name of the reference.
          if (isEmpty(fieldMap)) {
            if (this.refExists(fieldName, "addError")) {
              // first lets clear the errors.
              this.clearRefErrors(fieldName);

              const ref = this.getRef(fieldName);
              ref.addError(errorMsg);
            }
          } else {
            let found = false;

            // Loop over every field in the map and check it.
            for (let j in fieldMap) {
              if (
                fieldMap.hasOwnProperty(j) &&
                fieldMap[j].toLowerCase() === fieldName.toLowerCase() &&
                this.refExists(j, "addError")
              ) {
                // first lets clear the errors.
                this.clearRefErrors(j);

                const ref = this.getRef(j);
                ref.addError(errorMsg);
                found = true;
              }
            }
            if (!found) {
              otherErrors.push(validationErrors[i]);
            }
          }
        }
      }

      /*
       * Parse each non-validation error
       */
      if (genericErrorDataField !== null) {
        let errors = [];
        for (let i in otherErrors) {
          if (
            otherErrors.hasOwnProperty(i) &&
            (otherErrors[i] instanceof BaseError ||
              otherErrors[i] instanceof API.SimpleAPIErrors.SimpleAPIError)
          ) {
            errors.push(otherErrors[i].getMessage());
          }
        }
        this.$set(this, genericErrorDataField, errors);
      }
    }
  }
};
